import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, X, FileText, Building, Users, CheckCircle, AlertCircle, Download, Loader } from 'lucide-react';
import * as XLSX from 'xlsx';
import { format } from 'date-fns';
import { parseErrorMessage } from '../utils/helpers';

const BulkImporter = ({
  onImportCompanies = () => {},
  onImportContacts = () => {},
  existingCompanies = [],
  existingContacts = [],
  onRefreshData = () => {}
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [file, setFile] = useState(null);

  const downloadTemplate = () => {
    // Crear plantilla con 2 hojas: Empresas, Contactos
    const workbook = XLSX.utils.book_new();

    // Hoja 1: Empresas
    const companiesHeaders = ['Nombre', 'Razón Social', 'Industria', 'Ubicación'];
    const companiesData = [
      ['TechCorp Solutions', 'TechCorp Solutions S.A.', 'Tecnología', 'Buenos Aires'],
      ['Retail Plus', 'Retail Plus S.R.L.', 'Retail', 'Córdoba']
    ];
    const companiesSheet = XLSX.utils.aoa_to_sheet([companiesHeaders, ...companiesData]);
    XLSX.utils.book_append_sheet(workbook, companiesSheet, 'Empresas');

    // Hoja 2: Contactos
    const contactsHeaders = ['Nombre', 'Email', 'Teléfono', 'Cargo', 'Empresa', 'Estado', 'Notas'];
    const contactsData = [
      ['Juan Pérez', 'juan.perez@techcorp.com', '+54 11 1234-5678', 'Director Comercial', 'TechCorp Solutions', 'Cliente', 'Contacto principal'],
      ['María González', 'maria.gonzalez@retailplus.com', '+54 351 9876-5432', 'Gerente de Compras', 'Retail Plus', 'Lead', 'Interesado en nuevos productos']
    ];
    const contactsSheet = XLSX.utils.aoa_to_sheet([contactsHeaders, ...contactsData]);
    XLSX.utils.book_append_sheet(workbook, contactsSheet, 'Contactos');

    // Ajustar anchos de columnas
    companiesSheet['!cols'] = [{ wch: 30 }, { wch: 30 }, { wch: 25 }, { wch: 25 }];
    contactsSheet['!cols'] = [{ wch: 25 }, { wch: 30 }, { wch: 20 }, { wch: 25 }, { wch: 25 }, { wch: 15 }, { wch: 40 }];

    const fileName = `plantilla_importacion_masiva_${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setImportResults(null);
    }
  };

  const findHeader = (headers, possibleNames) => {
    const normalizedHeaders = headers.map(h => h ? h.toString().trim().toLowerCase().replace(/[^a-z0-9]/gi, '') : '');
    for (const name of possibleNames) {
      const normalizedName = name.toLowerCase().replace(/[^a-z0-9]/gi, '');
      const index = normalizedHeaders.indexOf(normalizedName);
      if (index !== -1) {
        return headers[index];
      }
    }
    return null;
  };

  const processImport = async () => {
    if (!file) {
      alert('Seleccioná un archivo Excel antes de continuar.');
      return;
    }

    setImporting(true);
    setImportResults(null);

    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const binaryString = event.target.result;
          const workbook = XLSX.read(binaryString, { type: 'binary' });
          
          const results = {
            companies: { imported: 0, skipped: 0, errors: [] },
            contacts: { imported: 0, skipped: 0, errors: [] }
          };

          // Listas actualizadas (se actualizan durante la importación)
          let currentCompanies = [...existingCompanies];

          // Procesar Empresas
          if (workbook.SheetNames.includes('Empresas')) {
            const companiesSheet = workbook.Sheets['Empresas'];
            const companiesJson = XLSX.utils.sheet_to_json(companiesSheet, { header: 1 });
            
            if (companiesJson.length > 1) {
              const headers = companiesJson[0].map(h => h ? h.toString().trim() : '');
              const nombreHeader = findHeader(headers, ['nombre', 'name']);
              
              if (nombreHeader) {
                for (let i = 1; i < companiesJson.length; i++) {
                  const row = companiesJson[i];
                  const rowData = {};
                  headers.forEach((header, colIndex) => {
                    rowData[header] = row[colIndex];
                  });

                  const nombre = rowData[nombreHeader];
                  if (!nombre || nombre.toString().trim() === '') {
                    results.companies.skipped++;
                    continue;
                  }

                  // Verificar si ya existe
                  const exists = currentCompanies.find(c => 
                    c.name.toLowerCase().trim() === nombre.toString().toLowerCase().trim()
                  );

                  if (exists) {
                    results.companies.skipped++;
                    results.companies.errors.push(`Fila ${i + 1}: Empresa "${nombre}" ya existe`);
                    continue;
                  }

                  try {
                    const razonSocialHeader = findHeader(headers, ['razón social', 'razon social', 'razonsocial']);
                    const industriaHeader = findHeader(headers, ['industria', 'rubro', 'industry']);
                    const ubicacionHeader = findHeader(headers, ['ubicación', 'ubicacion', 'location']);

                    const companyData = {
                      id: Date.now().toString() + Math.random().toString(36).substr(2, 9) + i,
                      name: nombre.toString().trim(),
                      razonSocial: razonSocialHeader && rowData[razonSocialHeader] 
                        ? rowData[razonSocialHeader].toString().trim() 
                        : '',
                      industry: industriaHeader && rowData[industriaHeader]
                        ? rowData[industriaHeader].toString().trim()
                        : 'Sin especificar',
                      location: ubicacionHeader && rowData[ubicacionHeader]
                        ? rowData[ubicacionHeader].toString().trim()
                        : ''
                    };

                    const importedCompany = await onImportCompanies(companyData);
                    // Agregar a la lista actual para usar en contactos
                    if (importedCompany) {
                      currentCompanies.push(importedCompany);
                    }
                    results.companies.imported++;
                  } catch (error) {
                    results.companies.errors.push(`Fila ${i + 1}: ${error.message}`);
                  }
                }
              }
            }
          }

          // Actualizar lista de empresas después de importarlas
          if (onRefreshData) {
            await onRefreshData();
            // Esperar un momento para que se actualicen los datos
            await new Promise(resolve => setTimeout(resolve, 500));
          }

          // Procesar Contactos (después de empresas para poder relacionarlos)
          if (workbook.SheetNames.includes('Contactos')) {
            const contactsSheet = workbook.Sheets['Contactos'];
            const contactsJson = XLSX.utils.sheet_to_json(contactsSheet, { header: 1 });
            
            if (contactsJson.length > 1) {
              const headers = contactsJson[0].map(h => h ? h.toString().trim() : '');
              const nombreHeader = findHeader(headers, ['nombre', 'name']);
              const emailHeader = findHeader(headers, ['email', 'correo']);
              
              if (nombreHeader && emailHeader) {
                for (let i = 1; i < contactsJson.length; i++) {
                  const row = contactsJson[i];
                  const rowData = {};
                  headers.forEach((header, colIndex) => {
                    rowData[header] = row[colIndex];
                  });

                  const nombre = rowData[nombreHeader];
                  const email = rowData[emailHeader];
                  
                  if (!nombre || nombre.toString().trim() === '' || !email || email.toString().trim() === '') {
                    results.contacts.skipped++;
                    continue;
                  }

                  // Buscar empresa por nombre (usar lista actualizada)
                  const empresaHeader = findHeader(headers, ['empresa', 'company', 'nombreempresa']);
                  let companyId = null;
                  if (empresaHeader && rowData[empresaHeader]) {
                    const empresaNombre = rowData[empresaHeader].toString().trim();
                    const company = currentCompanies.find(c => 
                      c.name.toLowerCase().trim() === empresaNombre.toLowerCase().trim()
                    );
                    if (company) {
                      companyId = company.id;
                    } else {
                      results.contacts.errors.push(`Fila ${i + 1}: Empresa "${empresaNombre}" no encontrada para contacto "${nombre}"`);
                    }
                  }

                  const telefonoHeader = findHeader(headers, ['teléfono', 'telefono', 'phone']);
                  const cargoHeader = findHeader(headers, ['cargo', 'position', 'puesto']);
                  const estadoHeader = findHeader(headers, ['estado', 'status']);
                  const notasHeader = findHeader(headers, ['notas', 'notes', 'observaciones']);

                  try {
                    const contactData = {
                      id: Date.now().toString() + Math.random().toString(36).substr(2, 9) + i,
                      name: nombre.toString().trim(),
                      email: email.toString().trim(),
                      phone: telefonoHeader && rowData[telefonoHeader] 
                        ? rowData[telefonoHeader].toString().trim() 
                        : '',
                      position: cargoHeader && rowData[cargoHeader]
                        ? rowData[cargoHeader].toString().trim()
                        : '',
                      companyIds: companyId ? [companyId] : [],
                      status: estadoHeader && rowData[estadoHeader]
                        ? rowData[estadoHeader].toString().trim()
                        : 'Lead',
                      notes: notasHeader && rowData[notasHeader]
                        ? rowData[notasHeader].toString().trim()
                        : '',
                      lastContact: new Date()
                    };

                    const importedContact = await onImportContacts(contactData);
                    results.contacts.imported++;
                  } catch (error) {
                    results.contacts.errors.push(`Fila ${i + 1}: ${error.message}`);
                  }
                }
              }
            }
          }

          setImportResults(results);
        } catch (error) {
          alert(`No se pudo procesar el archivo.\n${parseErrorMessage(error, 'Verificá que el formato del Excel sea correcto e intentá de nuevo.')}`);
        } finally {
          setImporting(false);
        }
      };
      reader.readAsBinaryString(file);
    } catch (error) {
      alert(`No se pudo leer el archivo.\n${parseErrorMessage(error, 'Verificá que el archivo no esté dañado e intentá de nuevo.')}`);
      setImporting(false);
    }
  };

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              className="hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
            />
            <div
              className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm"
            >
              <div
                className="bg-white rounded-xl w-full max-h-[88vh] overflow-y-auto"
              >
                <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between z-10">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-blue-600 rounded-xl">
                      <Upload className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-800">Importación Masiva</h2>
                      <p className="text-sm text-gray-500">Importa empresas y contactos desde Excel</p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setIsOpen(false);
                      setFile(null);
                      setImportResults(null);
                    }}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="w-6 h-6 text-gray-500" />
                  </button>
                </div>

                <div className="p-6 space-y-6">
                  {/* Instrucciones */}
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                      <FileText className="w-5 h-5" />
                      Instrucciones
                    </h3>
                    <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                      <li>El archivo Excel debe tener 2 hojas: "Empresas" y "Contactos"</li>
                      <li>Las empresas se importan primero y son necesarias para relacionar contactos</li>
                      <li>Los contactos pueden estar relacionados con empresas por nombre</li>
                      <li>Descarga la plantilla para ver el formato exacto</li>
                    </ul>
                  </div>

                  {/* Descargar plantilla */}
                  <div className="flex justify-end">
                    <button
                      onClick={downloadTemplate}
                      className="px-4 py-2 bg-gray-100 text-gray-700 border border-gray-200 rounded-lg flex items-center gap-2 hover:bg-gray-200 transition-colors"
                    >
                      <Download className="w-4 h-4" />
                      Descargar Plantilla
                    </button>
                  </div>

                  {/* Selección de archivo */}
                  <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center">
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleFileSelect}
                      className="hidden"
                      id="bulk-import-file"
                      disabled={importing}
                    />
                    <label
                      htmlFor="bulk-import-file"
                      className={`cursor-pointer flex flex-col items-center gap-4 ${importing ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <Upload className="w-12 h-12 text-gray-400" />
                      {file ? (
                        <div>
                          <p className="text-sm font-medium text-gray-700">{file.name}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {(file.size / 1024).toFixed(2)} KB
                          </p>
                        </div>
                      ) : (
                        <div>
                          <p className="text-sm font-medium text-gray-700">
                            Haz clic para seleccionar un archivo Excel
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            Solo se admiten archivos .xlsx y .xls
                          </p>
                        </div>
                      )}
                    </label>
                  </div>

                  {/* Botón de importar */}
                  {file && (
                    <div className="flex justify-end">
                      <button
                        onClick={processImport}
                        disabled={importing}
                        className={`px-6 py-3 rounded-lg font-semibold flex items-center gap-2 ${
                          importing
                            ? 'bg-gray-400 text-white cursor-not-allowed'
                            : 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm'
                        }`}
                      >
                        {importing ? (
                          <>
                            <Loader className="w-5 h-5 animate-spin" />
                            Importando...
                          </>
                        ) : (
                          <>
                            <Upload className="w-5 h-5" />
                            Iniciar Importación
                          </>
                        )}
                      </button>
                    </div>
                  )}

                  {/* Resultados */}
                  {importResults && (
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-gray-800">Resultados de la Importación</h3>
                      
                      {/* Empresas */}
                      <div className="bg-white border border-gray-200 rounded-xl p-4">
                        <div className="flex items-center gap-3 mb-3">
                          <Building className="w-5 h-5 text-blue-500" />
                          <h4 className="font-semibold text-gray-800">Empresas</h4>
                        </div>
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <span className="text-gray-600">Importadas:</span>
                            <span className="ml-2 font-semibold text-green-600">
                              {importResults.companies.imported}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-600">Omitidas:</span>
                            <span className="ml-2 font-semibold text-yellow-600">
                              {importResults.companies.skipped}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-600">Errores:</span>
                            <span className="ml-2 font-semibold text-red-600">
                              {importResults.companies.errors.length}
                            </span>
                          </div>
                        </div>
                        {importResults.companies.errors.length > 0 && (
                          <div className="mt-3 text-xs text-red-600 space-y-1">
                            {importResults.companies.errors.slice(0, 5).map((error, idx) => (
                              <div key={idx}>• {error}</div>
                            ))}
                            {importResults.companies.errors.length > 5 && (
                              <div>... y {importResults.companies.errors.length - 5} más</div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Contactos */}
                      <div className="bg-white border border-gray-200 rounded-xl p-4">
                        <div className="flex items-center gap-3 mb-3">
                          <Users className="w-5 h-5 text-green-500" />
                          <h4 className="font-semibold text-gray-800">Contactos</h4>
                        </div>
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <span className="text-gray-600">Importados:</span>
                            <span className="ml-2 font-semibold text-green-600">
                              {importResults.contacts.imported}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-600">Omitidos:</span>
                            <span className="ml-2 font-semibold text-yellow-600">
                              {importResults.contacts.skipped}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-600">Errores:</span>
                            <span className="ml-2 font-semibold text-red-600">
                              {importResults.contacts.errors.length}
                            </span>
                          </div>
                        </div>
                        {importResults.contacts.errors.length > 0 && (
                          <div className="mt-3 text-xs text-red-600 space-y-1">
                            {importResults.contacts.errors.slice(0, 5).map((error, idx) => (
                              <div key={idx}>• {error}</div>
                            ))}
                            {importResults.contacts.errors.length > 5 && (
                              <div>... y {importResults.contacts.errors.length - 5} más</div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Resumen total */}
                      <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-semibold text-gray-800">Total Importado</p>
                            <p className="text-sm text-gray-600">
                              {importResults.companies.imported + importResults.contacts.imported} registros
                            </p>
                          </div>
                          <CheckCircle className="w-8 h-8 text-green-500" />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </AnimatePresence>

      <button
        onClick={() => setIsOpen(true)}
        className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold shadow-sm transition-colors flex items-center gap-2"
      >
        <Upload className="w-5 h-5" />
        Importación Masiva
      </button>
    </>
  );
};

export default BulkImporter;

